import { useEffect, useState } from "react";
import {
  BookCheck,
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  FileText,
  Loader2,
  MapPin,
  RotateCcw,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { Hint } from "@/client/components/common/hint";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { Input } from "@/client/components/ui/input";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { Textarea } from "@/client/components/ui/textarea";
import type { FragmentLinkRole } from "@/client/db";
import {
  closePlacementReview,
  dismissPlacement,
  openPlacementReview,
  prunePlacements,
  usePlacementState,
  useRequestPlacements,
} from "@/client/hooks/use-placement-suggestions";
import type { Placement } from "@/client/hooks/use-story-ai";
import { useStoryData } from "@/client/hooks/use-story-data";
import { targetLabel } from "@/client/hooks/use-story-outline";
import { formatWordCount } from "@/client/lib/word-count";
import { RoleIcon, RoleToggle } from "./fragment-role";
import { PlaceFragmentPopover } from "./place-fragment-popover";
import { useAcceptPlacement } from "./placement-suggestions";

const KIND_LABELS = { scene: "Scene", chapter: "Whole chapter", newScene: "New scene" } as const;
const KIND_HINTS = {
  scene: "Use the idea in this scene. Word for word, its words go in as written; as inspiration, it shapes the scene.",
  chapter:
    "The idea runs through the whole chapter rather than one scene, like a mood, a secret, or a thread. Drafts of every scene in the chapter see it.",
  newScene:
    "No scene fits it yet, so the AI suggests a new one. Creating it adds the scene at the end of the chapter and uses the idea there. You can move it later.",
} as const;
const KIND_ICONS = { scene: FileText, chapter: BookOpen, newScene: FilePlus2 } as const;

function SuggestionCard({
  storyId,
  fragmentId,
  placement,
}: {
  storyId: string;
  fragmentId: string;
  placement: Placement;
}) {
  const { outline, fragments } = useStoryData();
  const accept = useAcceptPlacement(storyId);
  const [role, setRole] = useState<FragmentLinkRole>(placement.role);
  const [title, setTitle] = useState(placement.kind === "newScene" ? placement.title : "");
  const [summary, setSummary] = useState(placement.kind === "newScene" ? (placement.summary ?? "") : "");
  const [busy, setBusy] = useState(false);
  const Icon = KIND_ICONS[placement.kind];

  const scene = placement.kind === "scene" ? outline.sceneById.get(placement.sceneId) : undefined;
  const chapter = outline.chapterById.get(
    placement.kind === "scene" ? (scene?.chapterId ?? "") : placement.chapterId
  );
  const alreadyHere =
    placement.kind === "scene"
      ? (fragments.linksByScene.get(placement.sceneId)?.length ?? 0)
      : placement.kind === "chapter"
        ? (fragments.linksByChapter.get(placement.chapterId)?.length ?? 0)
        : 0;
  const words = scene ? (outline.wordCounts.get(scene.id) ?? 0) : 0;

  const use = async () => {
    setBusy(true);
    try {
      await accept(fragmentId, placement, { role, title, summary: summary.trim() || null });
    } catch {
      // Reported by the collection
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 size-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Hint title={KIND_LABELS[placement.kind]} description={KIND_HINTS[placement.kind]}>
              <span className="cursor-help font-semibold">{KIND_LABELS[placement.kind]}</span>
            </Hint>
            {chapter && placement.kind !== "chapter" && <span className="normal-case tracking-normal">in {chapter.title}</span>}
          </div>

          {placement.kind === "newScene" ? (
            <div className="space-y-2 pt-1">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="New scene title" className="h-8 font-medium" />
              <Textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                aria-label="New scene summary"
                placeholder="What happens in it"
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">It's added at the end of the chapter. You can move it later.</p>
            </div>
          ) : (
            <>
              <p className="font-medium">
                {placement.kind === "scene" ? (scene?.title ?? "A deleted scene") : (chapter?.title ?? "A deleted chapter")}
              </p>
              {placement.kind === "scene" ? (
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {scene?.summary?.trim() || "No summary yet."}
                </p>
              ) : (
                chapter?.notes?.trim() && (
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{chapter.notes}</p>
                )
              )}
              <p className="text-xs text-muted-foreground">
                {[
                  placement.kind === "scene" && (words > 0 ? formatWordCount(words) : "Nothing written yet"),
                  placement.kind === "chapter" &&
                    `${chapter?.scenes.length ?? 0} ${chapter?.scenes.length === 1 ? "scene" : "scenes"}`,
                  alreadyHere > 0 && `${alreadyHere} ${alreadyHere === 1 ? "idea" : "ideas"} already here`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </>
          )}
        </div>
      </div>

      {placement.kind === "scene" && placement.checked && (
        <Hint
          title={placement.checked === "already" ? "Already in the scene's text" : "Checked against the scene's text"}
          description={
            placement.checked === "already"
              ? "The AI read what's written in this scene and found the idea is already there. Linking it records where it's used; nothing new gets written."
              : "The AI read what's written in this scene, not just its summary, and the idea fits with it."
          }
        >
          <p className="flex w-fit cursor-help items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            {placement.checked === "already" ? <BookCheck className="size-3.5" /> : <Check className="size-3.5" />}
            {placement.checked === "already" ? "Already in the scene's text" : "Checked against the scene's text"}
          </p>
        </Hint>
      )}

      {placement.reason && (
        <div className="flex gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-primary" />
          <p className="whitespace-pre-wrap">{placement.reason}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <RoleToggle role={role} onChange={setRole} />
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => dismissPlacement(fragmentId, placement)} disabled={busy}>
            <X />
            Skip
          </Button>
          <Button size="sm" onClick={use} disabled={busy || (placement.kind === "newScene" && !title.trim())}>
            {busy ? <Loader2 className="animate-spin" /> : <Check />}
            {placement.kind === "newScene"
              ? "Create and use"
              : placement.kind === "scene" && placement.checked === "already"
                ? "Link it here"
                : "Use here"}
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * The AI's suggestions for one fragment at a time, in full, with the
 * fragment itself and where it's already used. Steps through every fragment
 * that has suggestions.
 */
export function PlacementReviewDialog({ storyId }: { storyId: string }) {
  const { reviewing, reviewOrder, byFragment, pending } = usePlacementState(storyId);
  const { outline, fragments } = useStoryData();

  // Suggestions for ideas deleted, archived or placed since go away
  useEffect(() => {
    if (!fragments.isLoading) prunePlacements(storyId, fragments);
  }, [storyId, fragments, reviewing]);
  const requestPlacements = useRequestPlacements(storyId);
  const sequence = reviewOrder.filter((id) => fragments.byId.has(id));
  const index = reviewing ? sequence.indexOf(reviewing) : -1;
  const fragment = reviewing ? fragments.byId.get(reviewing) : undefined;
  const placements = reviewing ? (byFragment.get(reviewing) ?? []) : [];
  const links = reviewing ? (fragments.linksByFragment.get(reviewing) ?? []) : [];
  const remaining = sequence.filter((id) => (byFragment.get(id)?.length ?? 0) > 0);
  const nextWithSuggestions = sequence.slice(index + 1).find((id) => (byFragment.get(id)?.length ?? 0) > 0);

  return (
    <Dialog open={!!reviewing && !!fragment} onOpenChange={(open) => !open && closePlacementReview()}>
      <DialogContent className="flex max-h-[88vh] flex-col gap-4 sm:max-w-4xl">
        <DialogHeader className="flex-row items-start justify-between gap-4 space-y-0 pr-8">
          <div className="space-y-1">
            <DialogTitle>Where does this belong?</DialogTitle>
            <DialogDescription>
              {remaining.length > 1
                ? `${remaining.length} ideas have suggested places. Take each place or leave it.`
                : "Take a suggested place, change the role first, or leave it."}
            </DialogDescription>
          </div>
          {sequence.length > 1 && (
            <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                disabled={index <= 0}
                onClick={() => openPlacementReview(sequence[index - 1])}
                aria-label="Previous idea"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="tabular-nums">
                {index + 1} of {sequence.length}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                disabled={index >= sequence.length - 1}
                onClick={() => openPlacementReview(sequence[index + 1])}
                aria-label="Next idea"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </DialogHeader>

        {fragment && reviewing && (
          <div className="grid min-h-0 flex-1 gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div className="flex min-h-0 flex-col gap-3">
              <div className="max-h-[45vh] overflow-y-auto rounded-lg border bg-muted/30">
                <p className="whitespace-pre-wrap p-4 text-sm leading-relaxed">{fragment.content}</p>
              </div>
              {links.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Already used in</p>
                  <ul className="flex flex-wrap gap-1">
                    {links.map((link) => {
                      const label = targetLabel(outline, link);
                      return (
                        <li
                          key={link.id}
                          className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          <RoleIcon role={link.role} />
                          {label.title}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pending.has(reviewing)}
                  onClick={() => requestPlacements([reviewing])}
                >
                  {pending.has(reviewing) ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                  Ask again
                </Button>
                <PlaceFragmentPopover storyId={storyId} fragmentId={reviewing}>
                  <Button size="sm" variant="outline">
                    <MapPin />
                    Somewhere else…
                  </Button>
                </PlaceFragmentPopover>
              </div>
            </div>

            <ScrollArea className="min-h-0 overflow-hidden">
              {placements.length > 0 ? (
                <ul className="space-y-3 pr-3">
                  {placements.map((placement) => (
                    <SuggestionCard
                      key={`${reviewing}:${placement.kind}:${"sceneId" in placement ? placement.sceneId : placement.chapterId}:${placement.kind === "newScene" ? placement.title : ""}`}
                      storyId={storyId}
                      fragmentId={reviewing}
                      placement={placement}
                    />
                  ))}
                </ul>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  <Check className="size-6 text-primary" />
                  <p>{links.length > 0 ? "Placed. No suggestions left for this one." : "No suggestions left for this one."}</p>
                  {nextWithSuggestions ? (
                    <Button size="sm" onClick={() => openPlacementReview(nextWithSuggestions)}>
                      Next idea
                      <ChevronRight />
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={closePlacementReview}>
                      Done
                    </Button>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
